// ===============================================
// 🎯 SUPPORT DASHBOARD - TÉCNICO CATERPILLAR
// ===============================================

import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Settings,
  Search,
  Phone,
  Mail,
  Bot,
  Users,
  TrendingUp,
  RefreshCw,
  Filter,
  Star,
  MessageSquare,
  BookOpen,
  Wrench,
  Shield,
  Zap
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// ===============================================
// 🔧 INTERFACES
// ===============================================

interface SupportTicket {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  motor: string;
  configuration: string;
  component: string;
  created_at: string;
  updated_at: string;
  assigned_to?: string;
  user_name: string;
}

interface SupportMetrics {
  total_tickets: number;
  open_tickets: number;
  resolved_today: number;
  avg_response_time: string;
  satisfaction_score: number;
  active_chats: number;
}

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  motor?: string;
  configuration?: string;
  views: number;
  helpful_votes: number;
}

// ===============================================
// 📊 COMPONENTE PRINCIPAL
// ===============================================

const SupportDashboard: React.FC = () => {
  const { isDark } = useTheme();
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [metrics, setMetrics] = useState<SupportMetrics | null>(null);
  const [faqs, setFAQs] = useState<FAQItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // ===============================================
  // 🎨 CLASES DE ESTILO
  // ===============================================

  const cardClasses = `rounded-lg shadow-md p-6 transition-all duration-200 ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-800';
  const subTextClasses = isDark ? 'text-gray-400' : 'text-gray-600';
  const labelClasses = isDark ? 'text-gray-300' : 'text-gray-700';

  // ===============================================
  // 🔄 EFECTOS Y CARGA DE DATOS
  // ===============================================

  useEffect(() => {
    loadSupportData();
  }, []);

  const loadSupportData = async () => {
    setLoading(true);
    try {
      // Simular carga de datos del backend
      await Promise.all([
        loadTickets(),
        loadMetrics(),
        loadFAQs()
      ]);
    } catch (error) {
      console.error('Error cargando datos de soporte:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTickets = async () => {
    // Simulación - reemplazar con llamada real al backend
    const mockTickets: SupportTicket[] = [
      {
        id: '1',
        title: 'Problema con válvulas motor C27',
        description: 'Válvulas del motor C27 configuración L4G no funcionan correctamente',
        status: 'open',
        priority: 'high',
        motor: 'C27',
        configuration: 'L4G',
        component: 'válvulas',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        user_name: 'Juan Pérez'
      },
      {
        id: '2',
        title: 'Mantenimiento preventivo C175',
        description: 'Consulta sobre cronograma de mantenimiento',
        status: 'in_progress',
        priority: 'medium',
        motor: 'C175',
        configuration: 'AJ8',
        component: 'sistema',
        created_at: new Date(Date.now() - 86400000).toISOString(),
        updated_at: new Date().toISOString(),
        user_name: 'María García'
      }
    ];
    setTickets(mockTickets);
  };

  const loadMetrics = async () => {
    // Simulación - reemplazar con llamada real al backend
    const mockMetrics: SupportMetrics = {
      total_tickets: 156,
      open_tickets: 23,
      resolved_today: 8,
      avg_response_time: '2.3 horas',
      satisfaction_score: 4.7,
      active_chats: 5
    };
    setMetrics(mockMetrics);
  };

  const loadFAQs = async () => {
    // Simulación - reemplazar con llamada real al backend
    const mockFAQs: FAQItem[] = [
      {
        id: '1',
        question: '¿Cómo verificar el estado de las válvulas en motor C27?',
        answer: 'Para verificar válvulas en C27: 1) Revise presión de aceite, 2) Inspeccione ruidos anormales...',
        category: 'Diagnóstico',
        motor: 'C27',
        views: 245,
        helpful_votes: 42
      },
      {
        id: '2',
        question: '¿Cuándo cambiar filtros en motor C175?',
        answer: 'Los filtros del C175 deben cambiarse cada 500 horas de operación o según manual...',
        category: 'Mantenimiento',
        motor: 'C175',
        views: 189,
        helpful_votes: 38
      }
    ];
    setFAQs(mockFAQs);
  };

  // ===============================================
  // 🎯 FUNCIONES AUXILIARES
  // ===============================================

  const getPriorityColor = (priority: string) => {
    const colors = {
      low: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[priority as keyof typeof colors] || colors.low;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      open: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-purple-100 text-purple-800'
    };
    return colors[status as keyof typeof colors] || colors.open;
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      open: <AlertCircle className="w-4 h-4" />,
      in_progress: <Clock className="w-4 h-4" />,
      resolved: <CheckCircle className="w-4 h-4" />,
      closed: <CheckCircle className="w-4 h-4" />
    };
    return icons[status as keyof typeof icons] || icons.open;
  };

  // ===============================================
  // 🎨 SECTIONS NAVIGATION
  // ===============================================

  const sections = [
    { id: 'overview', label: 'Resumen', icon: TrendingUp }
  ];

  // ===============================================
  // 🎨 RENDER
  // ===============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className={textClasses}>Cargando panel de soporte...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* ✅ HEADER */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            
            <div>
              <h1 className={`text-3xl font-bold ${textClasses}`}>
                Dashboard
              </h1>
              
            </div>
          </div>
          
          <button
            onClick={loadSupportData}
            disabled={loading}
            className={`px-4 py-2 rounded-md font-medium transition-colors duration-200 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            } disabled:opacity-50`}
          >
            <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>

        {/* ✅ NAVIGATION TABS */}
        <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex space-x-8">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeSection === section.id
                      ? 'border-blue-500 text-blue-600'
                      : `border-transparent ${
                          isDark 
                            ? 'text-gray-400 hover:text-gray-300' 
                            : 'text-gray-500 hover:text-gray-700'
                        }`
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ✅ CONTENT SECTIONS */}
      {activeSection === 'overview' && (
        <div className="space-y-6">
          {/* Métricas Principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className={cardClasses}>
              <div className="flex items-center">
                <FileText className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className={`text-2xl font-bold ${textClasses}`}>
                    {metrics?.total_tickets}
                  </p>
                  <p className={`text-sm ${subTextClasses}`}>Total Tickets</p>
                </div>
              </div>
            </div>

            <div className={cardClasses}>
              <div className="flex items-center">
                <AlertCircle className="w-8 h-8 text-orange-600 mr-3" />
                <div>
                  <p className={`text-2xl font-bold ${textClasses}`}>
                    {metrics?.open_tickets}
                  </p>
                  <p className={`text-sm ${subTextClasses}`}>Tickets Abiertos</p>
                </div>
              </div>
            </div>

            <div className={cardClasses}>
              <div className="flex items-center">
                <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                <div>
                  <p className={`text-2xl font-bold ${textClasses}`}>
                    {metrics?.resolved_today}
                  </p>
                  <p className={`text-sm ${subTextClasses}`}>Resueltos Hoy</p>
                </div>
              </div>
            </div>

            <div className={cardClasses}>
              <div className="flex items-center">
                <Clock className="w-8 h-8 text-purple-600 mr-3" />
                <div>
                  <p className={`text-2xl font-bold ${textClasses}`}>
                    {metrics?.avg_response_time}
                  </p>
                  <p className={`text-sm ${subTextClasses}`}>Tiempo Promedio</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tickets Recientes */}
          <div className={cardClasses}>
            <h3 className={`text-lg font-semibold ${textClasses} mb-4`}>
              Tickets Recientes
            </h3>
            <div className="space-y-3">
              {tickets.slice(0, 3).map((ticket) => (
                <div
                  key={ticket.id}
                  className={`flex items-center justify-between p-3 rounded-md ${
                    isDark ? 'bg-gray-700' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(ticket.status)}
                    <div>
                      <p className={`font-medium ${textClasses}`}>
                        {ticket.title}
                      </p>
                      <p className={`text-sm ${subTextClasses}`}>
                        {ticket.motor} - {ticket.configuration} - {ticket.user_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      


    </div>
  );
};

export default SupportDashboard;